import { getGlobal, isNodeJS } from '../compat';
import { Logger, LogLevels } from './Logger';

// Re-export log levels, so calling code only needs to import from one file.
export { LogLevels };

const DB_NAME = 'dt_logs';
const MD_STORE_NAME = 'metadata';
const MD_SESSION_KEY = 'session';
const MD_SESSION_STORE_MAP_KEY = 'sessions';
const NUM_STORED_SESSIONS = 4; // Changing this requires to recreate the database, which is currently not implemented.

const DEFAULT_LEVEL = LogLevels.WARNING;

/**
 * This was inspired by https://github.com/seriyvolk83/logs2indexeddb.
 * A specialization of the Logger class that also writes log messages to IndexedDB.
 * It defaults to the warning level and persists the last 4 sessions.
 * The log level only affects console logging. Messages of all levels are persisted to the database.
 * If IndexedDB is not available or writing to it fails, it falls backs to just console logging.
 * Works across multiple instances and even different workers (if the same session id is provided to all instances).
 * The overhead of logging to IndexedDB should be neglectable in general, but it may be better
 * to not use this class in places that spam messages (e.g. one for each element / fragment).
 */
export class Logger2IndexedDb extends Logger {
    #loggerName;
    #indexedDB;
    /** @type {IDBDatabase} */
    #db;
    #consoles;
    #openPromise;
    #messageQueue = [];
    #sessionId;
    #sessionStoreName;

    /**
     * Create a new logger instance.
     * @param {String} [sessionId] An id for the current application / viewer session. It has to be the same
     *  for all logger instances in a session, even across workers. If no id is provided, logging to IDB is disabled
     *  for the logger instance.
     * @param {String} [loggerName] The name of the logger instance. Defaults to 'default'.
     */
    constructor(sessionId, loggerName = 'default') {
        super();

        this.#sessionId = sessionId;
        this.#loggerName = loggerName;

        this.#indexedDB = isNodeJS() ? null : getGlobal().indexedDB;
        let skipWarning = false;
        if (!this.#sessionId) {
            console.warn(`Logger ${this.#loggerName} created without session id. Will disable IndexedDB support.`);
            this.#indexedDB = null;
            skipWarning = true;
        }
        if (!this.#indexedDB && !skipWarning) {
            console.warn('Logging to indexedDB is not supported. Will only log to console.');
        }

        this.#consoles = {
            // Logger that saves data into opened IndexedDB
            indexeddb: {
                log: (...args) => {
                    this.#write2db('log', ...args);
                },
                warn: (...args) => {
                    this.#write2db('warn', ...args);
                },
                error: (...args) => {
                    this.#write2db('error', ...args);
                },
                info: (...args) => {
                    this.#write2db('info', ...args);
                },
                debug: (...args) => {
                    this.#write2db('debug', ...args);
                },
            },
            // Logs into both - console and indexeddb
            both: {
                log: (...args) => {
                    console.log(...args);
                    this.#consoles.indexeddb.log(...args);
                },
                warn: (...args) => {
                    console.warn(...args);
                    this.#consoles.indexeddb.warn(...args);
                },
                error: (...args) => {
                    console.error(...args);
                    this.#consoles.indexeddb.error(...args);
                },
                info: (...args) => {
                    console.info(...args);
                    this.#consoles.indexeddb.info(...args);
                },
                debug: (...args) => {
                    console.log(...args);
                    this.#consoles.indexeddb.debug(...args);
                }
            }
        }

        this.setLevel(DEFAULT_LEVEL);

        this.#openDb(DB_NAME);
    }

    setName(name) {
        this.#loggerName = name;
    }

    /**
     * This may be used if the calling code wants to wait for the logger to be ready (i.e. IDB opened and prepared)
     * before using it. It's optional (log calls made prior to successful initialization will be cached), but allows to
     * handle potential initialization failures.
     * @returns {Promise} A promise that resolves if the logger was initialized successfully, and rejects otherwise.
     */
    getInitPromise() {
        return this.#openPromise;
    }

    setLevel(level) {
        if (this.level === level)
            return;

        this.level = level;

        const consoleNothing = () => {};

        // If a logging method is disabled, we still want to log to IDB, if supported.
        let logDisabled;
        // The parent class constructor calls this before we can define this.#indexedDB in our own constructor,
        // which causes an exception. So we try catch that...
        try {
            if (this.#indexedDB) {
                logDisabled = this.#consoles.indexeddb;
            } else {
                logDisabled = {};
                for (const key in this.#consoles.both) {
                    logDisabled[key] = consoleNothing;
                }
            }
        } catch (e) {
            this.level = -1;
            return;
        }

        // If a logging method is enabled, we want to log to both the console and IDB, if supported.
        // Otherwise, fall back to console only.
        const logEnabled = this.#indexedDB ? this.#consoles.both : console;

        // Bind to console
        this.debug = level >= LogLevels.DEBUG   ? logEnabled.debug  : logDisabled.debug;
        this.log   = level >= LogLevels.LOG     ? logEnabled.log  : logDisabled.log;
        this.info  = level >= LogLevels.INFO    ? logEnabled.info : logDisabled.info;
        this.warn  = level >= LogLevels.WARNING ? logEnabled.warn : logDisabled.warn;
        this.error = level >= LogLevels.ERROR   ? logEnabled.error : logDisabled.error;
    }

    #disableIDBLogging() {
        console.warn('Disabling logging to IndexedDB. Will only log to console.');
        // This indicates that IDB logging should be disabled.
        this.#indexedDB = null;
        this.#db = null;
        // We need to reset the level, to avoid further logging to IDB if previously configured.
        const level = this.level;
        this.level = -1
        this.setLevel(level);
    }

    #handleDbError(error) {
        console.error('IDB Logger error:', error);
        this.#disableIDBLogging();
    }

    #openDb(dbName) {
        if (!this.#indexedDB) {
            let openReject;
            this.#openPromise = new Promise((resolve, reject) => { openReject = reject; });
            // We catch this to prevent 'Uncaught (in promise) ...' error messages in the browser console
            // if calling code doesn't await / catch the promise.
            this.#openPromise.catch(() => {});
            openReject();
            return this.#openPromise;
        }

        if (this.#openPromise) {
            return this.#openPromise;
        }

        // Open the database
        let openResolve, openReject;
        try {
            this.#openPromise = new Promise((resolve, reject) => {
                openResolve = (db) => {
                    this.#db = db;
                    for (const message of this.#messageQueue) {
                        this.#db.transaction(this.#sessionStoreName, 'readwrite').objectStore(this.#sessionStoreName).add(message);
                    }
                    this.#messageQueue = null;
                    resolve();
                };
                openReject = () => {
                    this.#messageQueue = null;
                    // We catch this to prevent 'Uncaught (in promise) ...' error messages in the browser console
                    // if calling code doesn't await / catch the promise.
                    this.#openPromise.catch(() => {});
                    reject();
                };
            });
            const openRequest = this.#indexedDB.open(dbName, 1);

            openRequest.onerror = (event) => {
                console.log(event);
                this.#handleDbError(event.target.error);
                openReject();
            };

            openRequest.onsuccess = (event) => {
                const db = event.target.result;

                // We define a global error handler on the db level, so there is no need for error handlers on
                // individual requests (unless we can handle / recover from individual errors).
                db.onerror = (event) => {
                    this.#handleDbError(event.target.error);
                };

                // Figure out which store to use for this session.
                // Starting a readwrite transaction makes sure that this is thread-safe.
                // If multiple instances of this logger (in different workers) start the transaction,
                // they will get queued up and executed one after another.
                const mdStore = db.transaction(MD_STORE_NAME, 'readwrite').objectStore(MD_STORE_NAME);
                mdStore.get(MD_SESSION_KEY).onsuccess = (event) => {
                    const mdVersion = event.target.result;
                    if (mdVersion === this.#sessionId) {
                        // Another logger instance set up the session already.
                        // Just get the store name for the current session.
                        mdStore.get(MD_SESSION_STORE_MAP_KEY).onsuccess = (event) => {
                            this.#sessionStoreName = event.target.result.current.toString();
                            openResolve(db);
                        }
                    } else {
                        // This is the first logger instance in this session.
                        // Prepare the database by rotating the log stores.
                        mdStore.get(MD_SESSION_STORE_MAP_KEY).onsuccess = (event) => {
                            try {
                                // This is a map of the form { current: name1, prev1: name2, ..., prevN: nameN },
                                // where current points to the store name of the previous(!) session.
                                // We need to shift all store names in rotation, e.g. prev1 = name1, ..., and current = nameN.
                                const sessionStoreMap = event.target.result;
                                const storeNamesOrdered = [sessionStoreMap.current];
                                for (let i = 1; i < NUM_STORED_SESSIONS; ++i) {
                                    storeNamesOrdered.push(sessionStoreMap['prev' + i]);
                                }
                                // Remember the store name for this session (prevN rotates into current).
                                this.#sessionStoreName = storeNamesOrdered[NUM_STORED_SESSIONS - 1];

                                // Clear the store for this session.
                                db.transaction(this.#sessionStoreName, 'readwrite').objectStore(this.#sessionStoreName).clear();

                                // Update the session store map.
                                sessionStoreMap.current = this.#sessionStoreName;
                                for (let i = 1; i < NUM_STORED_SESSIONS; ++i) {
                                    sessionStoreMap['prev' + i] = storeNamesOrdered[i - 1];
                                }
                                mdStore.put(sessionStoreMap, MD_SESSION_STORE_MAP_KEY).onsuccess = () => {
                                    // Last thing to do is to update the current session in the metadata table.
                                    mdStore.put(this.#sessionId, MD_SESSION_KEY).onsuccess = () => {
                                        // Done initializing.
                                        openResolve(db);
                                    };
                                };
                            } catch(e) {
                                this.#handleDbError(e);
                                openReject();
                            }
                        };
                    }
                };
            };

            // This is fired if the database doesn't exist yet, or if we specified a new version when opening it.
            openRequest.onupgradeneeded = (event) => {
                try {
                    /**
                     * @type {IDBDatabase}
                     */
                    const db = event.target.result;

                    // Delete all object stores in the database.
                    for (let storeName of db.objectStoreNames) {
                        db.deleteObjectStore(storeName);
                    }

                    // Create an object store for metadata.
                    const metadataStore = db.createObjectStore(MD_STORE_NAME);

                    // Store current session and sessions map.
                    metadataStore.add(this.#sessionId, MD_SESSION_KEY);

                    const sessionStoreMap = { current: '0' };
                    for (let i = 1; i < NUM_STORED_SESSIONS; ++i) {
                        sessionStoreMap['prev' + i] = (NUM_STORED_SESSIONS - i).toString();
                    }
                    metadataStore.add(sessionStoreMap, MD_SESSION_STORE_MAP_KEY);

                    // Create stores.
                    for (let i = 0; i < NUM_STORED_SESSIONS; ++i) {
                        db.createObjectStore(i.toString(), { autoIncrement: true });
                    }
                } catch(e) {
                    this.#handleDbError(e);
                    openReject();
                }
            };
        } catch(e) {
            this.#handleDbError(e);
            openReject();
        }

        return this.#openPromise;
    }

    #write2db(label, ...args) {
        if (!this.#indexedDB) {
            return;
        }

        try {
            // Attempt to stringify objects in the log parameters to avoid useless [object Object] outputs.
            const stringifiedArgs = [];
            for (let arg of args) {
                if (arg instanceof Error) {
                    // Explicitly include error stack traces in the log.
                    stringifiedArgs.push(arg, arg.stack);
                } else if (arg instanceof Object) {
                    let stringified;
                    try {
                        stringified = JSON.stringify(arg);
                    } catch(e) {
                        stringified = arg;
                    }
                    stringifiedArgs.push(stringified);
                } else {
                    stringifiedArgs.push(arg);
                }
            }
            const logString = stringifiedArgs.join(' ');
            const data = {
                label: label,
                loggerName: this.#loggerName,
                timestamp: Date.now(),
                log: logString
            };

            if (!this.#db) {
                // The db is not initialized yet, so we need to queue up the message until it is.
                this.#messageQueue.push(data);
                return;
            }

            this.#db.transaction(this.#sessionStoreName, 'readwrite').objectStore(this.#sessionStoreName).add(data);
        } catch(e) {
            console.error('IDB Logger error:', e);
            this.#disableIDBLogging();
        }
    }

    async downloadLogs() {
        const result = {};
        let resultResolve;
        const resultPromise = new Promise((resolve) => {
            resultResolve = (result = {}) => {
                resolve(result);
            };
        });

        const handleError = (e) => {
            console.error('Failed to download IDB logs:', e);
            resultResult();
        }

        if (!this.#indexedDB) {
            resultResolve();
            return resultPromise;
        }

        try {
            await this.#openPromise;

            // Read metadata to get store names and session order.
            const mdStore = this.#db.transaction(MD_STORE_NAME, 'readwrite').objectStore(MD_STORE_NAME)
            mdStore.get(MD_SESSION_STORE_MAP_KEY).onsuccess = (event) => {
                const sessionStoreMap = event.target.result;

                try {
                    const transaction = this.#db.transaction(Object.values(sessionStoreMap), 'readwrite');

                    transaction.oncomplete = () => {
                        resultResolve(result);
                    }

                    for (let storeName in sessionStoreMap) {
                        transaction.objectStore(sessionStoreMap[storeName]).getAll().onsuccess = (event) => {
                            result[storeName] = event.target.result;
                        }
                    }
                } catch(e) {
                    handleError(e);
                    return resultPromise;
                }
            };
        } catch(e) {
            handleError(e);
            return resultPromise;
        }

        return resultPromise;
    }
}

/** @type Logger2IndexedDb */
let logger;
/**
 * A singleton factory getter for a logger instance.
 * All invocations in the same thread will return the same logger instance.
 * @param {String} [sessionId] An id for the current application / viewer session. It has to be the same
 *  for all logger instances in a session, even across workers. If no id is provided, logging to IDB is disabled
 *  for the logger instance.
 *  Only the first call to this method in each thread will use this.
 * @param {String} [name] An optional name for the logger instance.
 *  Only the first call to this method in each thread will use this.
 * @param {Number} [level] An optional log level.
 *  It will be set if it's higher than the current log level of the logger instance.
 * @returns {Logger2IndexedDb}
 */
export function getLogger(sessionId, name, level) {
    if (!logger) {
        logger = new Logger2IndexedDb(sessionId, name);
    }

    if (level !== undefined && level > logger.level) {
        logger.setLevel(level);
    }

    return logger;
}
